import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Layers,
  FileEdit,
  CalendarDays,
  ClipboardList,
  Clock,
  Network,
  Settings,
  Rocket,
} from "lucide-react";
import "./brandDetails.css";
import { AppRoute } from "../../types/routes";

// Import Tab Components
import Strategy from "./tabs/Strategy";
import Drafts from "./tabs/Drafts";
import History from "./tabs/History";
import Connections from "./tabs/Connections";
import useBrandDetails from "../../hooks/use-brand-details";
import { useBrandSettingsModal } from "../../hooks/useBrandSettingsModal";
import Spinner from "../uiComponents/Spinner";
import Growth from "./tabs/Growth";
import BrandSettingsModal from "./BrandSettingsModal";
import Toast from "../uiComponents/Toast";
import { ContentLog } from "../../types/dashboard";
import ContentCalendar from "./tabs/creator-studio/ContentCalendar";
import PostAnalyticsScreen from "./tabs/PostAnalyticsScreen";
import usePfmPostSocialApi from "../../api/use-pfm-post-social.api";
import useBrandHistoryApi from "../../api/useBrandHistoryApi";
import { dispatchBrandHistoryRefresh } from "../../utils/brand-history-events";
import { dispatchWeeklyPlanRefresh } from "../../utils/weekly-plan-events";
import WeeklyPlan from "./tabs/WeeklyPlan";
import { WeeklyPlanItem } from "../../types/weekly-plan";
import { normalizeSocialPlatformId } from "../../utils/platform-post-util";

type GrowthTab = "monitor" | "seo" | "leads";

const BrandDetails: React.FC = () => {
  const location = useLocation();
  const navigateRouter = useNavigate();
  const {
    activeTab,
    setActiveTab,
    brandFromState,
    refreshBrandDetails,
    loading,
  } = useBrandDetails();
  const appliedHistoryItemFromState = useRef(false);
  const initialBrandReconcileRef = useRef<string | null>(null);

  const settingsModal = useBrandSettingsModal(
    brandFromState ?? null,
    refreshBrandDetails,
  );

  const [visitedTabs, setVisitedTabs] = useState<Set<string>>(
    new Set(["strategy"]),
  );
  const [openCreatorStudioAtStep, setOpenCreatorStudioAtStep] = useState<
    number | null
  >(null);
  const [selectedHistoryItem, setSelectedHistoryItem] =
    useState<ContentLog | null>(null);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [prefilledScheduleDate, setPrefilledScheduleDate] = useState<
    Date | undefined
  >();
  const [draftPrefill, setDraftPrefill] = useState<{
    prompt?: string;
    platforms?: string[];
    scheduledDate?: Date;
    weeklyPlanItemId?: string;
  }>({});
  const [plannedSeoTopic, setPlannedSeoTopic] = useState<string | null>(null);
  const [plannedSeoItemId, setPlannedSeoItemId] = useState<string | null>(null);
  const [calendarEditItem, setCalendarEditItem] = useState<ContentLog | null>(
    null,
  );
  const [analyticsItem, setAnalyticsItem] = useState<ContentLog | null>(null);
  const [calendarToast, setCalendarToast] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);
  const [initialGrowthTab, setInitialGrowthTab] = useState<GrowthTab>("seo");

  const { deletePost } = usePfmPostSocialApi();
  const { triggerBrandHistoryReconcile } = useBrandHistoryApi();

  const handleOpenCreatorStudioAtStep = (step: number) => {
    setActiveTab("drafts");
    setOpenCreatorStudioAtStep(step);
  };

  const handleViewHistoryItem = useCallback(
    (item: ContentLog) => {
      setSelectedHistoryItem(item);
      setOpenCreatorStudioAtStep(3);
      setActiveTab("drafts");
    },
    [setActiveTab],
  );

  const handleAnalyticsItem = useCallback(
    (item: ContentLog) => {
      setAnalyticsItem(item);
      setActiveTab("analytics");
    },
    [setActiveTab],
  );

  const handleAnalyticsBack = useCallback(() => {
    setAnalyticsItem(null);
    setHistoryRefreshKey((k) => k + 1);
    setActiveTab("history");
  }, [setActiveTab]);

  const handleHistoryBack = useCallback(() => {
    setSelectedHistoryItem(null);
    setOpenCreatorStudioAtStep(null);
    setHistoryRefreshKey((k) => k + 1);
    setActiveTab("history");
  }, [setActiveTab]);

  const handleCalendarEditPost = useCallback(
    (log: ContentLog) => {
      setCalendarEditItem(log);
      setOpenCreatorStudioAtStep(3);
      setActiveTab("drafts");
    },
    [setActiveTab],
  );

  const handleCalendarEditBack = useCallback(() => {
    setCalendarEditItem(null);
    setOpenCreatorStudioAtStep(null);
    setPrefilledScheduleDate(undefined);
    setActiveTab("calendar");
  }, [setActiveTab]);

  const handleCalendarDeletePost = useCallback(
    async (log: ContentLog): Promise<boolean> => {
      const success = await deletePost(log.post_id);
      if (!success) {
        setCalendarToast({
          message: "Failed to delete post. Please try again.",
          type: "error",
        });
      } else {
        setCalendarToast({
          message: "Post deleted successfully.",
          type: "success",
        });
        setHistoryRefreshKey((k) => k + 1);
        dispatchWeeklyPlanRefresh(log.product_id);
      }
      return success;
    },
    [deletePost],
  );

  const handleCalendarScheduleNew = useCallback(
    (date: Date, mode: "ai" | "own") => {
      setPrefilledScheduleDate(date);
      setDraftPrefill({ scheduledDate: date });
      setActiveTab("drafts");
      if (mode === "own") {
        setOpenCreatorStudioAtStep(3);
      } else {
        setOpenCreatorStudioAtStep(1);
      }
    },
    [setActiveTab],
  );

  const handleOpenPlanItem = useCallback(
    (item: WeeklyPlanItem) => {
      if (item.content_type === "seo_article") {
        setPlannedSeoTopic(item.topic);
        setPlannedSeoItemId(item.id);
        setInitialGrowthTab("seo");
        setActiveTab("growth");
        return;
      }

      const plannedDate = new Date(item.planned_for);
      const normalizedPlatform = normalizeSocialPlatformId(item.platform);
      setDraftPrefill({
        prompt: `${item.topic}\n\n${item.brief}`,
        platforms: normalizedPlatform ? [normalizedPlatform] : undefined,
        scheduledDate: plannedDate,
        weeklyPlanItemId: item.id,
      });
      setPrefilledScheduleDate(plannedDate);
      setOpenCreatorStudioAtStep(1);
      setActiveTab("drafts");
    },
    [setActiveTab],
  );

  const handleSidebarBack = useCallback(() => {
    if (window.history.length > 1) {
      navigateRouter(-1);
      return;
    }
    navigateRouter(AppRoute.HOME, { replace: true });
  }, [navigateRouter]);

  const activeHistoryItem = selectedHistoryItem ?? calendarEditItem;
  const activeHistoryBack = selectedHistoryItem
    ? handleHistoryBack
    : calendarEditItem
      ? handleCalendarEditBack
      : undefined;

  useEffect(() => {
    const brandId = brandFromState?.id;
    if (!brandId || initialBrandReconcileRef.current === brandId) {
      return;
    }

    initialBrandReconcileRef.current = brandId;

    void triggerBrandHistoryReconcile(brandId).then((success) => {
      if (success) {
        dispatchBrandHistoryRefresh(brandId);
        dispatchWeeklyPlanRefresh(brandId);
      }
    });
  }, [brandFromState?.id, triggerBrandHistoryReconcile]);

  useEffect(() => {
    setVisitedTabs((prev) => {
      const newSet = new Set(prev);
      newSet.add(activeTab);
      return newSet;
    });
  }, [activeTab]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const requestedTabFromQuery = searchParams.get("tab");
    const shouldRefreshPfmAccounts = searchParams.get("pfm_refresh") === "1";

    if (requestedTabFromQuery === "connections") {
      setActiveTab("connections");
    }

    if (shouldRefreshPfmAccounts) {
      void refreshBrandDetails();
    }

    if (requestedTabFromQuery === "connections" || shouldRefreshPfmAccounts) {
      navigateRouter(location.pathname, {
        replace: true,
        state: location.state ?? {},
      });
    }
  }, [
    location.pathname,
    location.search,
    location.state,
    navigateRouter,
    refreshBrandDetails,
    setActiveTab,
  ]);

  useEffect(() => {
    const requestedTab = location.state?.activeTab as string | undefined;
    const requestedGrowthTab = location.state?.growthTab as
      | GrowthTab
      | undefined;
    const historyItem = location.state?.historyItem as ContentLog | undefined;
    const analyticsStateItem = location.state?.analyticsItem as
      | ContentLog
      | undefined;

    if (requestedTab === "growth") {
      setActiveTab("growth");
      setInitialGrowthTab(requestedGrowthTab ?? "seo");
      navigateRouter(location.pathname, { replace: true, state: {} });
      return;
    }

    if (
      requestedTab &&
      ["strategy", "connections", "drafts", "weekly-plan", "history", "calendar"].includes(
        requestedTab
      )
    ) {
      setActiveTab(requestedTab);
      navigateRouter(location.pathname, { replace: true, state: {} });
      return;
    }

    if (analyticsStateItem && !appliedHistoryItemFromState.current) {
      appliedHistoryItemFromState.current = true;
      setAnalyticsItem(analyticsStateItem);
      setActiveTab("analytics");
      navigateRouter(location.pathname, { replace: true, state: {} });
      return;
    }

    if (historyItem && !appliedHistoryItemFromState.current) {
      appliedHistoryItemFromState.current = true;
      setSelectedHistoryItem(historyItem);
      setOpenCreatorStudioAtStep(3);
      setActiveTab("drafts");
      navigateRouter(location.pathname, { replace: true, state: {} });
    }
  }, [
    location.state?.activeTab,
    location.state?.growthTab,
    location.state?.analyticsItem,
    location.state?.historyItem,
    location.pathname,
    setActiveTab,
    navigateRouter,
  ]);

  if (loading && !brandFromState) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          width: "100%",
        }}
      >
        <Spinner style={{ display: "block" }} size="large" />
      </div>
    );
  }

  return (
    <div className="brand-details-container">
      {/* Sidebar */}
      <div className="brand-sidebar">
        <div className="sidebar-header">
          <button
            type="button"
            className="brand-avatar brand-avatar-back"
            onClick={handleSidebarBack}
            aria-label="Go back"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="brand-sidebar-meta">
            <div className="brand-name-sidebar">
              {brandFromState?.brand_name}
            </div>
            <div className="brand-role">
              {brandFromState?.marketing_strategy?.brand_url ? (
                <a
                  href={brandFromState.marketing_strategy.brand_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="brand-url-link"
                >
                  {brandFromState.marketing_strategy.brand_url}
                </a>
              ) : null}
            </div>
          </div>
        </div>

        <div className="sidebar-nav">
          <div
            className={`nav-item ${activeTab === "strategy" ? "active" : ""}`}
            onClick={() => setActiveTab("strategy")}
          >
            <div className="nav-icon">
              <Layers size={20} />
            </div>
            Brand Knowledge
          </div>
          <div
            className={`nav-item ${activeTab === "drafts" ? "active" : ""}`}
            onClick={() => setActiveTab("drafts")}
          >
            <div className="nav-icon">
              <FileEdit size={20} />
            </div>
            Creator Studio
          </div>
          <div
            className={`nav-item ${
              activeTab === "weekly-plan" ? "active" : ""
            }`}
            onClick={() => setActiveTab("weekly-plan")}
          >
            <div className="nav-icon">
              <ClipboardList size={20} />
            </div>
            Weekly Plan
          </div>
          <div
            className={`nav-item ${activeTab === "calendar" ? "active" : ""}`}
            onClick={() => setActiveTab("calendar")}
          >
            <div className="nav-icon">
              <CalendarDays size={20} />
            </div>
            Calendar
          </div>
          <div
            className={`nav-item ${activeTab === "growth" ? "active" : ""}`}
            onClick={() => setActiveTab("growth")}
            title={"Growth"}
          >
            <div className="nav-icon">{<Rocket size={20} />}</div>
            Growth
          </div>
          <div
            className={`nav-item ${activeTab === "history" ? "active" : ""}`}
            onClick={() => setActiveTab("history")}
          >
            <div className="nav-icon">
              <Clock size={20} />
            </div>
            History
          </div>
          <div
            className={`nav-item ${
              activeTab === "connections" ? "active" : ""
            }`}
            onClick={() => setActiveTab("connections")}
          >
            <div className="nav-icon">
              <Network size={20} />
            </div>
            Connections
          </div>
        </div>

        <div className="sidebar-footer">
          <div className="nav-item" onClick={settingsModal.openSettings}>
            <div className="nav-icon">
              <Settings size={20} />
            </div>
            Settings
          </div>
        </div>
      </div>

      <BrandSettingsModal {...settingsModal} />

      {settingsModal.toast?.isVisible && (
        <Toast
          message={settingsModal.toast.message}
          type={settingsModal.toast.type}
          onClose={() => settingsModal.setToast(null)}
          duration={3000}
        />
      )}

      {calendarToast && (
        <Toast
          message={calendarToast.message}
          type={calendarToast.type}
          onClose={() => setCalendarToast(null)}
          duration={3000}
        />
      )}

      {/* Main Content */}
      <div
        className={`brand-main-content ${
          activeTab === "drafts" ||
          activeTab === "weekly-plan" ||
          activeTab === "calendar" ||
          activeTab === "analytics"
            ? "full-width"
            : ""
        }`}
      >
        {brandFromState && (
          <>
            <div
              className="tab-content"
              style={{ display: activeTab === "strategy" ? "block" : "none" }}
            >
              {visitedTabs.has("strategy") && (
                <Strategy
                  brandData={brandFromState}
                  refreshBrandDetails={refreshBrandDetails}
                  onOpenCreatorStudio={() => handleOpenCreatorStudioAtStep(3)}
                />
              )}
            </div>

            <div
              className="tab-content"
              style={{ display: activeTab === "drafts" ? "block" : "none" }}
            >
              {visitedTabs.has("drafts") && (
                <Drafts
                  brandDetails={brandFromState}
                  openAtStep={openCreatorStudioAtStep}
                  onConsumedOpenAtStep={() => setOpenCreatorStudioAtStep(null)}
                  historyItem={activeHistoryItem}
                  onHistoryBack={activeHistoryBack}
                  prefilledScheduleDate={
                    draftPrefill.scheduledDate ?? prefilledScheduleDate
                  }
                  initialPrompt={draftPrefill.prompt}
                  prefilledPlatforms={draftPrefill.platforms}
                  weeklyPlanItemId={draftPrefill.weeklyPlanItemId}
                />
              )}
            </div>

            <div
              className="tab-content"
              style={{
                display: activeTab === "weekly-plan" ? "block" : "none",
              }}
            >
              {visitedTabs.has("weekly-plan") && (
                <WeeklyPlan
                  brandDetails={brandFromState}
                  onOpenPlanItem={handleOpenPlanItem}
                />
              )}
            </div>

            <div
              className="tab-content"
              style={{ display: activeTab === "calendar" ? "block" : "none" }}
            >
              {visitedTabs.has("calendar") && (
                <ContentCalendar
                  brandDetails={brandFromState}
                  onEditPost={handleCalendarEditPost}
                  onDeletePost={handleCalendarDeletePost}
                  onScheduleNewPost={handleCalendarScheduleNew}
                  onAnalyticsItem={handleAnalyticsItem}
                  onOpenPlanItem={handleOpenPlanItem}
                />
              )}
            </div>

            <div
              className="tab-content"
              style={{ display: activeTab === "growth" ? "block" : "none" }}
            >
              {visitedTabs.has("growth") && (
                <Growth
                  brandDetails={brandFromState}
                  refreshBrandDetails={refreshBrandDetails}
                  initialTab={initialGrowthTab}
                  plannedSeoTopic={plannedSeoTopic}
                  plannedSeoItemId={plannedSeoItemId}
                  onConsumedPlannedSeoTopic={() => {
                    setPlannedSeoTopic(null);
                    setPlannedSeoItemId(null);
                  }}
                />
              )}
            </div>

            <div
              className="tab-content"
              style={{ display: activeTab === "history" ? "block" : "none" }}
            >
              {visitedTabs.has("history") && (
                <History
                  key={historyRefreshKey}
                  brandId={brandFromState.id}
                  onViewItem={handleViewHistoryItem}
                  onAnalyticsItem={handleAnalyticsItem}
                  onDeleteItem={handleCalendarDeletePost}
                />
              )}
            </div>

            <div
              className="tab-content"
              style={{
                display: activeTab === "connections" ? "block" : "none",
              }}
            >
              {visitedTabs.has("connections") && (
                <Connections
                  brandDetails={brandFromState}
                  refreshBrandDetails={refreshBrandDetails}
                />
              )}
            </div>

            {activeTab === "analytics" && analyticsItem && (
              <div className="tab-content" style={{ display: "block" }}>
                <PostAnalyticsScreen
                  item={analyticsItem}
                  brandDetails={brandFromState}
                  onBack={handleAnalyticsBack}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BrandDetails;
